<template>
    <!-- 관리자 좌측 메뉴(LNB) — 사내 디자인 시스템 SideNav 와 같은 모양·수치(폭 248 / 접힘 64, 안쪽 좌우 12 · 위 16, 항목 40 · 하위 36, 항목 간 4).
         바깥 div 가 열(폭·배경을 끝까지), 안쪽 nav 가 화면 한 판(sticky)이다. 메뉴 구성은 admin-nav.js. -->
    <div class="ds-lnb" :class="{ collapsed }" :data-state="collapsed ? 'closed' : 'open'">
        <nav class="ds-lnb-inner" aria-label="주 메뉴">
            <div class="ds-lnb-brand">
                <router-link :to="{ name: 'ManagingAssessment' }" class="ds-lnb-mark"
                             :title="collapsed ? `QESG 관리자 · ${company_name || ''}` : 'QESG 관리자'">
                    <img src="@shared/assets/img/qesg_logo.png" alt="">
                </router-link>
                <span v-if="!collapsed" class="ds-lnb-brand-text">
                    <span class="ds-lnb-brand-title">QESG 관리자</span>
                    <span v-if="company_name" class="ds-lnb-brand-sub" :title="company_name">{{ company_name }}</span>
                </span>
            </div>

            <div class="ds-lnb-scroll">
                <template v-for="(section, si) in sections">
                    <!-- 접히면 구역 이름을 못 그리니 짧은 선으로 구역을 가른다 -->
                    <div v-if="collapsed && si > 0" :key="`sep-${si}`" class="ds-lnb-sep" aria-hidden="true"/>
                    <p v-if="!collapsed" :key="`label-${si}`" class="ds-lnb-section" :class="{ 'ds-lnb-section-gap': si > 0 }">
                        {{ section.label }}
                    </p>
                    <ul :key="`list-${si}`" class="ds-lnb-list">
                        <li v-for="item in section.items" :key="item.label">
                            <template v-if="item.children">
                                <!-- 그룹 줄에는 면을 깔지 않는다(지금 화면 표시는 아래 하위 항목이 한다). 접혔을 때만 예외 -->
                                <button type="button" class="ds-lnb-item"
                                        :class="{ 'is-group-active': isActive(item), 'is-current': collapsed && isActive(item) }"
                                        :aria-expanded="collapsed ? null : String(isOpen(item))"
                                        :aria-current="collapsed && isActive(item) ? 'true' : null"
                                        :title="collapsed ? item.label : null"
                                        @click="onGroupClick(item)">
                                    <AppIcon :name="item.icon" class="ds-lnb-icon"/>
                                    <span :class="collapsed ? 'sr-only' : 'ds-lnb-label'">{{ item.label }}</span>
                                    <AppIcon v-if="!collapsed" name="chevron-right" class="ds-lnb-chevron" :class="{ open: isOpen(item) }"/>
                                </button>
                                <ul v-if="!collapsed && isOpen(item)" class="ds-lnb-children">
                                    <li v-for="child in item.children" :key="child.to">
                                        <router-link :to="{ name: child.to }" class="ds-lnb-child" active-class="" exact-active-class=""
                                                     :class="{ 'is-current': isActive(child) }"
                                                     :aria-current="isActive(child) ? 'page' : null">
                                            <AppIcon :name="child.icon" class="ds-lnb-child-icon"/>
                                            <span class="ds-lnb-label">{{ child.label }}</span>
                                        </router-link>
                                    </li>
                                </ul>
                            </template>
                            <router-link v-else :to="{ name: item.to }" class="ds-lnb-item" active-class="" exact-active-class=""
                                         :class="{ 'is-current': isActive(item) }"
                                         :aria-current="isActive(item) ? 'page' : null"
                                         :title="collapsed ? item.label : null">
                                <AppIcon :name="item.icon" class="ds-lnb-icon"/>
                                <span :class="collapsed ? 'sr-only' : 'ds-lnb-label'">{{ item.label }}</span>
                            </router-link>
                        </li>
                    </ul>
                </template>
            </div>

            <div class="ds-lnb-account" :title="collapsed ? user_name : null">
                <span class="ds-lnb-avatar" aria-hidden="true">{{ (user_name || '?').slice(0, 1) }}</span>
                <span v-if="!collapsed" class="ds-lnb-account-name">{{ user_name }}</span>
            </div>
        </nav>
    </div>
</template>

<script>
import {mapState} from 'vuex';
import {visibleSections, isItemActive} from '../../layout/admin-nav';

export default {
    name: 'app-sidebar',
    props: {
        collapsed: {type: Boolean, default: false},
    },
    data() {
        return {
            // 손으로 펴거나 접은 그룹. 지금 화면이 든 그룹은 따로 열린다
            toggled: {},
        };
    },
    computed: {
        ...mapState(['is_admin']),
        user_name() {
            return this.$store.getters.getUserName;
        },
        company_name() {
            return this.$store.getters.getCompanyName;
        },
        sections() {
            return visibleSections(this.is_admin);
        },
    },
    watch: {
        // 화면이 바뀌면 손으로 접은 상태를 풀어 지금 화면의 그룹이 보이게 한다
        '$route.name'() {
            this.toggled = {};
        },
    },
    methods: {
        isActive(item) {
            return isItemActive(item, this.$route.name);
        },
        isOpen(item) {
            return item.label in this.toggled ? this.toggled[item.label] : this.isActive(item);
        },
        onGroupClick(item) {
            // 접힌 레일에서는 하위 목록을 그릴 자리가 없으니 첫 하위 화면으로 간다(DS 와 같은 동작)
            if (this.collapsed) {
                this.$router.push({name: item.children[0].to}).catch(() => {});
                return;
            }
            this.$set(this.toggled, item.label, !this.isOpen(item));
        },
    },
};
</script>

<style lang="scss">
.ds-lnb {
    flex-shrink: 0;
    align-self: stretch;
    width: 248px;
    background-color: var(--ds-sidebar);
    transition: width .2s cubic-bezier(.16, 1, .3, 1);

    &.collapsed {
        width: 64px;
    }
}

.ds-lnb-inner {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    height: 100vh;
    padding-top: 16px;
    color: var(--ds-sidebar-fg);
}

.ds-lnb-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 12px;
    padding: 8px 8px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, .1);

    .collapsed & {
        justify-content: center;
        padding-left: 0;
        padding-right: 0;
    }
}

.ds-lnb-mark {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: var(--ds-radius-card);
    background-color: var(--ds-surface);

    img {
        width: 22px;
        height: 22px;
    }

    &:hover {
        opacity: .8;
    }
}

.ds-lnb-brand-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.ds-lnb-brand-title {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
}

.ds-lnb-brand-sub {
    overflow: hidden;
    font-size: 13px;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--ds-sidebar-fg-muted);
}

.ds-lnb-scroll {
    flex: 1;
    min-height: 0;
    margin: 12px 12px 0;
    overflow-y: auto;
    // 메뉴 끝까지 굴려도 본문이 따라 움직이지 않게(DS 중첩 스크롤 가두기). 손잡이 색은 styles/ds/_scrollbar.scss
    overscroll-behavior: contain;
}

.ds-lnb-section {
    margin: 0;
    padding: 0 12px 8px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .025em;
    color: var(--ds-sidebar-fg-muted);

    &.ds-lnb-section-gap {
        margin-top: 16px;
    }
}

.ds-lnb-sep {
    width: 16px;
    height: 1px;
    margin: 8px auto;
    background-color: rgba(255, 255, 255, .1);
}

.ds-lnb-list,
.ds-lnb-children {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
}

.ds-lnb-children {
    padding-left: 16px;

    > li:first-child {
        margin-top: 4px;
    }
}

.ds-lnb-item,
.ds-lnb-child {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 0 12px;
    border: 0;
    border-radius: var(--ds-radius-card);
    background: transparent;
    color: inherit;
    text-align: left;
    text-decoration: none;
    transition: background-color .15s, color .15s;

    &:hover {
        background-color: var(--ds-sidebar-active);
        color: #fff;
        text-decoration: none;
    }

    &:focus-visible {
        outline: 2px solid var(--ds-sidebar-mark);
        outline-offset: -2px;
    }

    &.is-current {
        background-color: var(--ds-sidebar-active);
        color: #fff;
        font-weight: 500;
    }

    &.is-group-active {
        color: #fff;
        font-weight: 500;
    }
}

.ds-lnb-item {
    height: 40px;
    font-size: 14px;

    .collapsed & {
        justify-content: center;
        padding: 0;
    }
}

.ds-lnb-child {
    height: 36px;
    font-size: 13px;
}

.ds-lnb-icon {
    width: 18px;
    height: 18px;
}

.ds-lnb-child-icon {
    width: 16px;
    height: 16px;
}

.ds-lnb-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.ds-lnb-chevron {
    width: 16px;
    height: 16px;
    opacity: .6;
    transition: transform .2s;

    &.open {
        transform: rotate(90deg);
    }
}

.ds-lnb-account {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 12px;
    padding: 12px 8px 16px;
    border-top: 1px solid rgba(255, 255, 255, .1);
    font-size: 13px;

    .collapsed & {
        justify-content: center;
        padding-left: 0;
        padding-right: 0;
    }
}

.ds-lnb-avatar {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: var(--ds-sidebar-active);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
}

.ds-lnb-account-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
</style>
